import Card from 'components/Layout/Card';
import React, { useState } from 'react';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  fetchMemberships,
  fetchRequestEventData,
  getEvent,
  handleSendEventRequest,
} from 'store/slices';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/form/Button';
import CheckBox from 'components/form/CheckBox';
import { objectToFormData, getUserType } from 'utils/helpers';
import EventAcceptanceLetterheadRequestForm from './EventForm/EventAcceptanceLetterheadRequestForm';
import EventRequestForm from './EventForm/EventRequestForm';
import RegistrationEventRequestForm from './EventForm/RegistrationEventRequestForm';
import ServicesDataRequestForm from './EventForm/ServicesDataRequestForm';
import EventProcessSideSection from '../../../../EventManagement/Events/EventFormTab/EventProcessSideSection';
import usePermission from 'hooks/usePermission';

const ApprovalHub = ({ isViewMode, isEditMode, user_type }) => {
  const navigate = useNavigate();
  const userType = getUserType();
  const { createAccess } = usePermission("Requests");
  const tabOrder = [
    'events',
    'acceptance_letterhead',
    'registration',
    'add_on_service',
  ];
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('events');
  const [completedTabs, setCompletedTabs] = useState([]);
  const params = useParams();
  const [isFormLoading, setIsFormLoading] = useState();
  const {
    requestEventData,
    editDataAccess,
    is_visible_to_third_party,
  } = useSelector((state) => ({
    requestEventData: state.global.requestEventData?.event_process_bar || {},
    editDataAccess: state.global.requestEventData?.edit_access,
    is_visible_to_third_party:
      state.global.requestEventData?.is_visible_to_third_party,
  }));

  const [isCheckboxChecked, setIsCheckboxChecked] = useState(
    is_visible_to_third_party === '1' ? true : false
  );

  const fetchEventDetails = async () => {
    await dispatch(fetchMemberships());
    const response = await dispatch(fetchRequestEventData(params?.eventId));
    setIsFormLoading(false);
  };

  // Check if a tab is disabled
  const isTabDisabled = (key) => {
    if (isViewMode || isEditMode) return false;
    const currentIndex = tabOrder.indexOf(key);
    return (
      currentIndex > 0 && !completedTabs.includes(tabOrder[currentIndex - 1])
    );
  };

  // Handle moving to the next tab
  const handleNext = (currentKey) => {
    const currentIndex = tabOrder.indexOf(currentKey);
    const nextTab = tabOrder[currentIndex + 1];
    if (nextTab) {
      setCompletedTabs((prev) => [...prev, currentKey]);
      setActiveTab(nextTab);
    }
  };

  const handleSendEventRequestData = async () => {
    const payload = objectToFormData({
      event_id: params?.eventId,
      ...(isCheckboxChecked && { is_check: isCheckboxChecked }),
    });
    // Dispatch the action to send the request
    const response = await dispatch(handleSendEventRequest(payload));
  };

  const handleSendClick = () => {
    handleSendEventRequestData();
  };

  return (
    <>
      <Card>
        {/* Tabs Navigation */}
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => {
            if (!isTabDisabled(k)) setActiveTab(k);
          }}
          className="custom-tabs mb-2 gap-4 cpt-15 cps-15"
        >
          <Tab
            eventKey="events"
            title="Add Events"
            disabled={isTabDisabled('events')}
          />
          <Tab
            eventKey="acceptance_letterhead"
            title="Acceptance Letterhead"
            disabled={isTabDisabled('acceptance_letterhead')}
          />

          <Tab
            eventKey="registration"
            title="Registration"
            disabled={isTabDisabled('registration')}
          />
          <Tab
            eventKey="add_on_service"
            title="Add-On Services"
            disabled={isTabDisabled('add_on_service')}
          />
        </Tabs>

        <div className="row">
          <div className="col-md-9">
            {activeTab === 'events' && (
              <EventRequestForm
                handleNext={handleNext}
                disableNext={completedTabs.includes('events')}
                isViewMode={isViewMode}
              />
            )}
            {activeTab === 'acceptance_letterhead' && (
              <>
                <div className="cmt-20 cms-20 cmb-20">
                  <EventAcceptanceLetterheadRequestForm
                    eventId={params.eventId}
                    fetchEventDetails={fetchEventDetails}
                    eventTab
                    handleNext={handleNext}
                    isViewMode={isViewMode}
                  />
                </div>
              </>
            )}

            {activeTab === 'registration' && (
              <div className="cmt-20 cms-20">
                <RegistrationEventRequestForm
                  eventId={params.eventId}
                  eventTab
                  handleNext={handleNext}
                  isViewMode={isViewMode}
                  fetchEventDetails={fetchEventDetails}
                />
              </div>
            )}

            {activeTab === 'add_on_service' && (
              <div className="cmt-20 cms-20 cme-20 cmb-20">
                <ServicesDataRequestForm
                  eventId={params.eventId}
                  handleNext={handleNext}
                  isViewMode={isViewMode}
                  fetchEventDetails={fetchEventDetails}
                />
              </div>
            )}
          </div>

          {/* Right side: Sidebar/Status */}
          <div
            className="col-md-3 cps-20 "
            style={{ borderLeft: '1px solid #ccc' }}
          >
            <EventProcessSideSection
              tabOrder={tabOrder}
              isViewMode={isViewMode}
              event_id={params.eventId}
              type={activeTab}
              requestProcessFlowData={requestEventData}
              editBtnAccess={editDataAccess}
            />
          </div>
        </div>
      </Card>
      {['6'].includes(user_type) && !isViewMode && !isEditMode && (
        <div>
          <div className="d-flex align-items-center gap-2 cmt-18">
            <CheckBox
              type="PRIMARY-ACTIVE"
              onClick={() => {
                setIsCheckboxChecked(!isCheckboxChecked);
              }}
              isChecked={isCheckboxChecked}
            />
            <span>
              I confirm this information should be reflected on both the event
              website and the IFERP Dashboard.
            </span>
          </div>

          <div className="d-flex gap-3 cpt-15">
            {createAccess === '1' && (
              <Button
                text="Send Request"
                btnStyle="primary-dark"
                className="h-37 text-14-500 cps-20 cpe-20"
                onClick={handleSendClick}
              />
            )}
            <Button
              text="Cancel"
              btnStyle="light-outline"
              className="h-37 text-14-500 cps-20 cpe-20"
              onClick={() => navigate(-1)}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ApprovalHub;
